import { createTheme, type Theme } from '@mui/material/styles';
import { matchPath } from 'react-router-dom';
import type { TProductType } from 'typings/deviceTypes';
import { QUERY_PARAMS, ROUTES } from 'utils/constants';
import { getProductTypeStorage } from 'utils/sessionFunctions';
import type { TColorFamily } from './palette';

/**
 * Re-theming a page around one product.
 *
 * Several screens belong to a product rather than to the app as a whole — the
 * Pet plan flow, the Bird upsell — and should take that product's colour for
 * their page tint, their buttons and their gradient.
 *
 * Rather than have those pages reach for `palette.pet.main` in a hundred
 * places, they wrap in one of these and keep using the semantic names. MUI's
 * own components follow too, because `palette.primary` is re-pointed.
 */

export const PRODUCTS = ['home', 'bird', 'trail', 'pet', 'baby'] as const;
export type TProductName = (typeof PRODUCTS)[number];

export const isProduct = (v: unknown): v is TProductName =>
  typeof v === 'string' && (PRODUCTS as readonly string[]).includes(v);

/**
 * Maps a URL's first segment to a product.
 *
 * The route table is already product-segmented — /bird/*, /pet/*, /baby/*,
 * /trail/* — so the path is the most reliable signal available. Everything
 * else (dashboard, billing, login, /home/*, /data/*) belongs to Home.
 *
 * Deliberately reads only the first segment: /pet/subscription/:id/upgrade is
 * still a Pet page.
 */
const PATH_PRODUCT: Record<string, TProductName> = {
  bird: 'bird',
  pet: 'pet',
  baby: 'baby',
  trail: 'trail'
};

/**
 * The same lookup, but `null` when the path has no explicit product segment
 * instead of defaulting to `'home'` — for a caller that needs to tell "this
 * URL genuinely is Home" apart from "this URL doesn't say", like
 * PrivateRoute's app-bridge fallback (only step in when the route itself
 * has no opinion).
 */
export const productSegmentFromPath = (
  pathname: string
): TProductName | null => {
  const segment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return (segment && PATH_PRODUCT[segment]) || null;
};

export const productFromPath = (pathname: string): TProductName =>
  productSegmentFromPath(pathname) || 'home';

/**
 * Maps the app's PRODUCT_TYPE constant to a palette family, for the pages
 * that know their product from state rather than from the URL.
 */
const PRODUCT_TYPE_MAP: Record<string, TProductName> = {
  HomeSecurity: 'home',
  Bird: 'bird',
  Pet: 'pet',
  Trail: 'trail',
  Baby: 'baby'
};

export const productFromType = (productType?: string): TProductName =>
  (productType && PRODUCT_TYPE_MAP[productType]) || 'home';

/**
 * Routes that belong to a product but whose URL doesn't say which one — the
 * plan being purchased/downgraded is in Redux instead of the path.
 *
 * PAYMENT_SUCCESS is the payment polling / status screen (`/payment-success`);
 * it stays on the flow's product while it polls and shows the activation
 * popup. On these routes resolveThemeProduct below prefers the plan in state
 * over a stored app-bridge product_type, so they stay scoped to routes that
 * actually carry a `planSelected`.
 */
const STATE_THEMED_ROUTES = [
  ROUTES.REVIEW_PURCHASE,
  ROUTES.REVIEW_DOWNGRADE,
  ROUTES.PAYMENT_SUCCESS,
  ROUTES.CHECKOUT_SESSION
];

/**
 * The single, full priority chain for which product a page should theme as:
 *
 *   1. the route's own path segment (/pet/..., /bird/...)
 *   2. for STATE_THEMED_ROUTES only, the product of the plan in Redux
 *   3. the app-bridge `product_type` stashed in localStorage at /app-bridge
 *   4. 'home'
 *
 * Called once, at the root (RouteProductTheme) — resolving here rather than
 * deeper in the tree (as PrivateRoute alone used to) means the theme is
 * correct before CssBaseline and the Suspense fallback loader paint, instead
 * of snapping from Home to the real product the instant the lazy page
 * mounts.
 */
export const resolveThemeProduct = (
  pathname: string,
  stateThemedProductId?: string
): TProductName => {
  const routeProduct = productSegmentFromPath(pathname);
  if (routeProduct) return routeProduct;

  // The plan being purchased is a fresher signal than the product the app
  // was showing when it opened the webview (e.g. Home at /app-bridge, then a
  // Bird plan bought in-app), so on these routes it wins over the stored value.
  const isStateThemedRoute = STATE_THEMED_ROUTES.some((route) =>
    matchPath(route, pathname)
  );
  if (isStateThemedRoute && stateThemedProductId) {
    return productFromType(stateThemedProductId);
  }

  const appBridgeProductType = getProductTypeStorage(
    QUERY_PARAMS.PRODUCT_TYPE_QUERY
  );
  return appBridgeProductType ? productFromType(appBridgeProductType) : 'home';
};

/** Reverse of PRODUCT_TYPE_MAP — a theme product name back to the app's own
 * PRODUCT_TYPE enum string, for signals (sessionStorage, URLs) that speak in
 * that format rather than in TProductName. */
export const PRODUCT_TYPE_BY_PRODUCT: Record<TProductName, TProductType> = {
  home: 'HomeSecurity',
  bird: 'Bird',
  pet: 'Pet',
  trail: 'Trail',
  baby: 'Baby'
};

/**
 * Returns a copy of `theme` with every product-derived slot pointed at
 * `product`. Cheap enough to call in a render, but memoise it per product if
 * the page re-renders often — see useProductTheme.
 *
 * @example
 *   const petTheme = withProduct(theme, 'pet');
 *   // petTheme.palette.primary.main      -> Pet 500
 *   // petTheme.palette.background.default-> Pet 100 (light mode)
 *   // petTheme.palette.gradient.brand    -> the Pet gradient
 */
export const withProduct = (theme: Theme, product: TProductName): Theme => {
  const family = theme.palette[product] as TColorFamily | undefined;
  if (!family) return theme;

  return createTheme(theme, {
    palette: {
      // MUI's own components read `primary`; re-pointing it is what makes
      // buttons, checkboxes and links follow the product.
      primary: family,
      background: {
        default: family.surface,
        subtle: family.surfaceStrong
      },
      gradient: {
        /** The active product's gradient, under a stable name. */
        brand: family.gradient
      }
    }
  });
};

/**
 * Picks any token off the active product without naming the product.
 *
 * Useful for the handful of places that need a step the named slots do not
 * cover — a chart series, a custom illustration fill.
 *
 * @example
 *   const c = productTokens(theme, 'bird');
 *   c.color(300)   // Bird 300
 *   c.gradient()   // Bird gradient
 *   c.shadow(200)  // elevation 200 (shared, not per product)
 */
export const productTokens = (theme: Theme, product: TProductName) => {
  const family = theme.palette[product] as TColorFamily;
  return {
    color: (step: number) => family[step as keyof TColorFamily] as string,
    main: () => family.main,
    surface: () => family.surface,
    contrastText: () => family.contrastText,
    gradient: () => family.gradient,
    shadow: (step: number) => theme.palette.shadows[step]
  };
};
